import { useRef } from 'react';
import { Button, Tooltip } from '@mui/material';
import { WalkieTalkieIcon } from '../../features/agent-phone/components/WalkieTalkieIcon';

type PttHoldButtonProps = {
  disabled?: boolean;
  isTransmitting: boolean;
  isReply?: boolean;
  onPressStart: () => void;
  onPressEnd: () => void;
};

export function PttHoldButton({
  disabled,
  isTransmitting,
  isReply = false,
  onPressStart,
  onPressEnd,
}: PttHoldButtonProps) {
  const pressingRef = useRef(false);

  const label = isTransmitting
    ? 'Release to stop'
    : isReply
      ? 'Hold to reply'
      : 'Hold radio';

  const handlePointerDown = (e: React.PointerEvent) => {
    if (disabled || pressingRef.current) return;
    pressingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    onPressStart();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!pressingRef.current) return;
    pressingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    onPressEnd();
  };

  return (
    <Tooltip title={label}>
      <span>
        <Button
          variant={isTransmitting || isReply ? 'contained' : 'outlined'}
          size="small"
          disabled={disabled}
          aria-label={label}
          aria-pressed={isTransmitting}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={(e) => {
            if (pressingRef.current && !e.currentTarget.hasPointerCapture(e.pointerId)) {
              pressingRef.current = false;
              onPressEnd();
            }
          }}
          onContextMenu={(e) => e.preventDefault()}
          startIcon={<WalkieTalkieIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: '16px',
            px: 1.75,
            py: 0.65,
            minWidth: 0,
            touchAction: 'none',
            userSelect: 'none',
            fontWeight: 700,
            fontSize: '0.78rem',
            textTransform: 'none',
            borderColor: isTransmitting ? '#ff0000' : isReply ? '#1b5e20' : '#dadce0',
            color: isTransmitting || isReply ? '#fff' : '#3c4043',
            bgcolor: isTransmitting ? '#ff0000' : isReply ? '#2e7d32' : '#fff',
            boxShadow: isReply && !isTransmitting ? '0 0 0 0 rgba(46,125,50,0.45)' : 'none',
            animation:
              isReply && !isTransmitting ? 'pttReplyPulse 1.4s ease-in-out infinite' : 'none',
            '@keyframes pttReplyPulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(46,125,50,0.45)' },
              '50%': { boxShadow: '0 0 0 8px rgba(46,125,50,0)' },
            },
            '&:hover': {
              bgcolor: isTransmitting ? '#cc0000' : isReply ? '#1b5e20' : '#fff5f5',
              borderColor: isTransmitting ? '#cc0000' : isReply ? '#1b5e20' : '#ff0000',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: '#f1f3f4',
              color: '#9aa0a6',
              borderColor: '#e8eaed',
              animation: 'none',
            },
          }}
        >
          {isTransmitting ? 'Talking…' : isReply ? 'Hold to Reply' : 'Hold Radio'}
        </Button>
      </span>
    </Tooltip>
  );
}
